import React, { useEffect, useState } from "react";
import { useFormPrimitiveStyles } from "../forms/formPrimitives";
import { REFINED } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";

/**
 * Kunde-Picker (Kundentermin) — sucht Kontakte (GET /contact?search, debounced)
 * und liefert die gewählte kundeId. Optik im kvp-rd-Refined-Stil.
 *
 * Aus OutlookTermine.tsx extrahiert (SP2), damit das neue TerminFormWindow und
 * der Legacy-Dialog denselben Picker nutzen — keine Doppelpflege/Drift.
 */

/** Ausgewählter Kunde im Kundentermin-Picker. */
export interface KundeAuswahl {
    id: number;
    name: string;
}

export const kontaktName = (c: any): string =>
    c?.displayName ||
    `${c?.lastname ?? ""}${c?.firstName ? ", " + c.firstName : ""}`
        .replace(/^,\s*|,\s*$/g, "")
        .trim() ||
    `Kontakt ${c?.id}`;

export const KundePicker: React.FC<{
    value: KundeAuswahl | null;
    onChange: (k: KundeAuswahl | null) => void;
    /** Read-only (fremder Kalender ohne WRITE) — Suche/Ändern gesperrt. */
    disabled?: boolean;
    /**
     * SP5 (Entscheidung 67): im Create ist der Kunde OPTIONAL (Label ohne
     * Pflicht-Stern) — ohne Auswahl klassifiziert der Server aus den
     * Teilnehmern (classifyFromAttendees). Default false = Pflichtfeld-Optik.
     */
    optional?: boolean;
    /**
     * Feldbeschriftung. Der Termin-Dialog sagt „Kontakt" (PO 27.07.2026): dort
     * wird auch mit Interessenten und Produktpartnern terminiert, „Kunde" ist im
     * KMVS ein Status und nicht die Menge, aus der hier gewählt wird. Default
     * bleibt „Kunde", damit die Kundenakten-Zuweisung ihre Sprache behält.
     */
    label?: string;
    /**
     * „Nicht gefunden? Neu anlegen" unter der Suche, mit dem Suchtext als
     * Vorbelegung (Zuweisen-Dialog der Alten Kundenakten, PO 28.09.2026).
     * Ohne Callback gibt es den Link nicht.
     */
    onNeu?: (suchtext: string) => void;
}> = ({ value, onChange, disabled, optional = false, label = "Kunde", onNeu }) => {
    const s = useFormPrimitiveStyles();
    const [q, setQ] = useState("");
    const [results, setResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        if (value) return;
        const term = q.trim();
        if (term.length < 2) {
            setResults([]);
            return;
        }
        const ctrl = new AbortController();
        const t = setTimeout(() => {
            setSearching(true);
            fetch(apiUrl("contact", { search: term, limit: 12 }), { signal: ctrl.signal })
                .then((r) => r.json())
                .then((d) => setResults((Array.isArray(d) ? d : []).slice(0, 12)))
                .catch(() => {
                    /* abort/err ignorieren */
                })
                .finally(() => setSearching(false));
        }, 300);
        return () => {
            clearTimeout(t);
            ctrl.abort();
        };
    }, [q, value]);

    const labelSuffix = optional ? (
        <span style={{ color: REFINED.ink3, fontWeight: 400 }}> (optional)</span>
    ) : (
        <span className={s.req}> *</span>
    );

    if (value) {
        return (
            <div className={s.field}>
                <label className={s.label}>
                    {label}
                    {labelSuffix}
                </label>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        border: `1px solid ${REFINED.line}`,
                        borderRadius: 10,
                        padding: "9px 13px",
                        background: REFINED.white,
                    }}
                >
                    <span style={{ fontFamily: REFINED.fontUI, color: REFINED.ink, fontSize: "0.95rem" }}>
                        {value.name}
                    </span>
                    {!disabled && (
                        <button
                            type="button"
                            onClick={() => {
                                setQ("");
                                onChange(null);
                            }}
                            style={{
                                background: "none",
                                border: "none",
                                color: REFINED.aub,
                                cursor: "pointer",
                                fontFamily: REFINED.fontUI,
                                fontWeight: 600,
                                fontSize: "0.85rem",
                            }}
                        >
                            ändern
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={s.field}>
            <label className={s.label} htmlFor="kunde-search">
                {label}
                {labelSuffix}
            </label>
            <input
                id="kunde-search"
                className={s.input}
                autoComplete="off"
                placeholder={`${label} suchen …`}
                value={q}
                disabled={disabled}
                onChange={(e) => setQ(e.target.value)}
            />
            {searching && (
                <div style={{ fontSize: "0.8rem", color: REFINED.ink3, fontFamily: REFINED.fontUI }}>Suche …</div>
            )}
            {!searching && q.trim().length >= 2 && results.length === 0 && (
                <div style={{ fontSize: "0.8rem", color: REFINED.ink3, fontFamily: REFINED.fontUI }}>
                    Keine Treffer.
                </div>
            )}
            {onNeu && !disabled && !searching && (
                <button
                    type="button"
                    onClick={() => onNeu(q.trim())}
                    style={{
                        alignSelf: "flex-start",
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: REFINED.aub,
                        cursor: "pointer",
                        fontFamily: REFINED.fontUI,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                    }}
                >
                    {q.trim().length >= 2 ? `+ „${q.trim()}" als neuen Kontakt anlegen` : "+ Neuen Kontakt anlegen"}
                </button>
            )}
            {results.length > 0 && (
                <div
                    style={{
                        border: `1px solid ${REFINED.line}`,
                        borderRadius: 10,
                        overflow: "hidden",
                        background: REFINED.white,
                    }}
                >
                    {results.map((c) => (
                        <button
                            type="button"
                            key={c.id}
                            onClick={() => {
                                setQ("");
                                setResults([]);
                                onChange({ id: c.id, name: kontaktName(c) });
                            }}
                            style={{
                                display: "flex",
                                width: "100%",
                                textAlign: "left",
                                background: "none",
                                border: "none",
                                borderBottom: `1px solid ${REFINED.line}`,
                                padding: "9px 13px",
                                cursor: "pointer",
                                fontFamily: REFINED.fontUI,
                                fontSize: "0.9rem",
                                color: REFINED.ink,
                            }}
                        >
                            {kontaktName(c)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default KundePicker;
